/**
 * Let Go Service - API client for the Let Go / Exit Bin feature
 * Helps users identify items to donate with AI-powered suggestions
 */

import { directApi } from './api';

const letGoService = {
  /**
   * Get items suggested for letting go (not searched in 12+ months)
   * @param {number} limit - Maximum number of suggestions
   * @returns {Promise<{suggestions: Array, count: number}>}
   */
  getSuggestions: async (limit = 20) => {
    const response = await directApi.get(`/let-go/suggestions?limit=${limit}`);
    return response.data;
  },

  /**
   * Record user's decision about an item
   * @param {number} itemId - Item ID
   * @param {string} decision - 'keep', 'donate', or 'not_sure'
   * @returns {Promise<{success: boolean, item_id: number, decision: string}>}
   */
  recordDecision: async (itemId, decision) => {
    const response = await directApi.post('/let-go/decision', {
      item_id: itemId,
      decision: decision
    });
    return response.data;
  },

  /**
   * Get items in the exit bin (maybe_donate status)
   * @returns {Promise<{items: Array, count: number}>}
   */
  getExitBin: async () => {
    const response = await directApi.get('/let-go/exit-bin');
    return response.data;
  },

  /**
   * Retrieve an item from the exit bin (user changed their mind)
   * @param {number} itemId - Item ID
   * @returns {Promise<{success: boolean, item_id: number}>}
   */
  retrieveFromExitBin: async (itemId) => {
    const response = await directApi.post(`/let-go/retrieve/${itemId}`);
    return response.data;
  },

  /**
   * Confirm that an item has been donated (soft deletes the item)
   * @param {number} itemId - Item ID
   * @returns {Promise<{success: boolean, item_id: number}>}
   */
  confirmDonation: async (itemId) => {
    const response = await directApi.post(`/let-go/confirm-donate/${itemId}`);
    return response.data;
  },

  /**
   * Get AI recommendation for whether to keep or donate an item
   * @param {number} itemId - Item ID
   * @returns {Promise<{success: boolean, recommendation: string, reasoning: string, confidence: string}>}
   */
  getAIRecommendation: async (itemId) => {
    const response = await directApi.get(`/let-go/ai-recommendation/${itemId}`);
    return response.data;
  },

  /**
   * Get Let Go statistics for the user
   * @returns {Promise<{exit_bin_count: number, keep_count: number, donated_count: number, needs_review_count: number}>}
   */
  getStats: async () => {
    const response = await directApi.get('/let-go/stats');
    return response.data;
  },

  /**
   * Get user's Let Go preferences
   * @returns {Promise<{hide_exit_bin_from_search: boolean, let_go_email_reminders: boolean}>}
   */
  getPreferences: async () => {
    const response = await directApi.get('/let-go/preferences');
    return response.data;
  },

  /**
   * Update user's Let Go preferences
   * @param {Object} preferences - Preferences to update
   * @param {boolean} [preferences.hide_exit_bin_from_search] - Hide exit bin items from search
   * @param {boolean} [preferences.let_go_email_reminders] - Send email reminders
   * @returns {Promise<{success: boolean}>}
   */
  updatePreferences: async (preferences) => {
    const response = await directApi.put('/let-go/preferences', preferences);
    return response.data;
  },

  /**
   * Get groups of potentially duplicate items
   * @param {number} limit - Maximum number of duplicate groups
   * @returns {Promise<{duplicate_groups: Array, count: number}>}
   */
  getDuplicates: async (limit = 10) => {
    const response = await directApi.get(`/let-go/duplicates?limit=${limit}`);
    return response.data;
  },

  /**
   * Analyze duplicate items with AI vision to recommend which to keep
   * @param {Array<number>} itemIds - Array of item IDs to compare
   * @returns {Promise<{success: boolean, recommendation: number, reasoning: string, confidence: string}>}
   */
  analyzeDuplicates: async (itemIds) => {
    const response = await directApi.post('/let-go/duplicates/analyze', {
      item_ids: itemIds
    });
    return response.data;
  },

  /**
   * Run AI-powered scan to find items to let go
   * @param {number} limit - Maximum number of suggestions
   * @returns {Promise<{success: boolean, suggestions: Array, items_scanned: number, message: string}>}
   */
  runAIScan: async (limit = 20) => {
    const response = await directApi.post('/let-go/ai-scan', { limit });
    return response.data;
  },

  /**
   * Get weekly declutter scan settings
   * @returns {Promise<{weekly_declutter_scan: boolean, last_declutter_scan: string|null}>}
   */
  getWeeklyScanSettings: async () => {
    const response = await directApi.get('/let-go/weekly-scan-settings');
    return response.data;
  },

  /**
   * Update weekly declutter scan settings
   * @param {boolean} enabled - Whether to enable weekly scan
   * @returns {Promise<{success: boolean, weekly_declutter_scan: boolean}>}
   */
  updateWeeklyScanSettings: async (enabled) => {
    const response = await directApi.put('/let-go/weekly-scan-settings', {
      weekly_declutter_scan: enabled
    });
    return response.data;
  },

  /**
   * Check if weekly scan is due (called on app load)
   * @returns {Promise<{scan_due: boolean, enabled: boolean, last_scan: string|null, days_since_scan: number}>}
   */
  checkWeeklyScanDue: async () => {
    const response = await directApi.get('/let-go/check-weekly-scan');
    return response.data;
  }
};

export default letGoService;
